/**
 * Community Garden — First-Time User Onboarding Flow
 * 
 * Guided tour for new users with tooltips, highlights, and progressive disclosure.
 * Persists completion state in localStorage.
 */

export interface OnboardingStep {
  id: string;
  title: string;
  content: string;
  targetSelector?: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  action?: string;
  highlightPadding?: number;
}

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to your shared garden',
    content: 'Your Discord community shapes this world together. Here are the few controls you need to get oriented.',
    position: 'center',
    action: 'Continue'
  },
  {
    id: 'shared-world',
    title: 'One world, shaped together',
    content: 'Messages and reactions add shared points. Across 16 stages, the world gains paths, water, structures, rare plants, and changing skies.',
    targetSelector: '#garden-container canvas, #garden-container',
    position: 'bottom',
    highlightPadding: 20
  },
  {
    id: 'progress',
    title: 'Know what changes next',
    content: 'Progress separates the shared garden stage from your personal gardener rank and shows the next real unlock for each.',
    targetSelector: '[data-shell-action="progress"], [data-open-garden-progress]',
    position: 'top',
    highlightPadding: 8
  },
  {
    id: 'community',
    title: 'Find your people and your gardener',
    content: 'Community holds your rank, top gardeners, recent activity, notifications, quests, wall notes, and avatar customization. Higher ranks unlock more colors, wall posting, and the Legend crown.',
    targetSelector: '[data-shell-action="community"]',
    position: 'top',
    highlightPadding: 8
  },
  {
    id: 'ready',
    title: 'The garden is ready',
    content: 'Drag to explore, use Garden to return to the center, and check Progress whenever you want to see what the community is building toward.',
    position: 'center',
    action: 'Enter the garden'
  }
];

import {
  STORAGE_KEY,
  PROGRESS_KEY,
  SKIPPED_KEY,
  STORAGE_VERSION,
  saveProgress as stateSaveProgress,
  loadProgress as stateLoadProgress,
  markCompleted as stateMarkCompleted,
  hasCompleted as stateHasCompleted,
  markSkipped as stateMarkSkipped,
  wasSkipped as stateWasSkipped,
  clearSkipped as stateClearSkipped,
  getCompletedSteps as stateGetCompletedSteps,
  clearProgress as stateClearProgress,
  dispatchStateChange,
  isSessionExpired,
  clearCompletedSteps
} from '../onboarding-state.js';
import { analytics } from '../analytics.js';

// Re-export for backwards compatibility
export { STORAGE_KEY, PROGRESS_KEY, STORAGE_VERSION };

interface ProgressData {
  version: string;
  currentStepIndex: number;
  lastUpdated: string;
  stepIdsCompleted: string[];
  totalSteps: number;
}

export class Onboarding {
  private steps: OnboardingStep[];
  private currentStepIndex: number = 0;
  private isActive: boolean = false;
  private stepIdsCompleted: Set<string> = new Set();
  private tooltipElement: HTMLElement | null = null;
  private highlightElement: HTMLElement | null = null;
  private backdropElement: HTMLElement | null = null;
  private keyboardHandler: ((e: KeyboardEvent) => void) | null = null;
  private resizeHandler: (() => void) | null = null;
  private currentStepMissingTarget: boolean = false;
  private previousFocus: HTMLElement | null = null;
  private backgroundInertStates = new Map<HTMLElement, boolean>();

  constructor(steps: OnboardingStep[] = ONBOARDING_STEPS) {
    this.steps = steps;
  }

  /**
   * Check if user has completed onboarding
   */
  static hasCompleted(): boolean {
    return stateHasCompleted();
  }

  /**
   * Mark onboarding as completed
   */
  static markCompleted(): void {
    stateMarkCompleted();
  }

  /**
   * Reset onboarding (for testing or restart)
   */
  static reset(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(PROGRESS_KEY);
      localStorage.removeItem(SKIPPED_KEY);
      clearCompletedSteps();
    } catch (e) {
      console.warn('[onboarding] Failed to reset:', e);
    }
  }

  /**
   * Get saved progress from localStorage
   * @deprecated Use loadProgress from onboarding-state.js
   */
  static getProgress(): ProgressData | null {
    return stateLoadProgress() as ProgressData | null;
  }

  /**
   * Save current progress to localStorage
   */
  private static saveProgress(index: number, stepIdsCompleted: string[], totalSteps: number): void {
    stateSaveProgress(index, stepIdsCompleted, totalSteps);
  }

  /**
   * Mark onboarding as skipped (can resume later)
   */
  static markSkipped(): void {
    stateMarkSkipped();
  }

  /**
   * Check if onboarding was skipped
   */
  static wasSkipped(): boolean {
    return stateWasSkipped();
  }

  /**
   * Get list of completed step IDs
   */
  static getCompletedSteps(): string[] {
    return stateGetCompletedSteps();
  }

  /**
   * Start the onboarding flow
   * REC-006: Improved resumability with session expiry handling
   */
  start(): void {
    if (this.isActive) return;
    
    // Check if already completed
    if (Onboarding.hasCompleted()) {
      if (import.meta.env.DEV) {
        console.log('[onboarding] Already completed, skipping');
      }
      return;
    }

    this.isActive = true;
    stateClearSkipped();
    this.previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.lockBackground();
    
    // Restore saved progress or start from beginning
    const savedProgress = Onboarding.getProgress();
    
    // REC-006: Check for session expiry - restart if expired
    if (savedProgress && isSessionExpired()) {
      if (import.meta.env.DEV) {
        console.log('[onboarding] Session expired, restarting from beginning');
      }
      this.currentStepIndex = 0;
      this.stepIdsCompleted = new Set();
      // Clear expired progress
      Onboarding.reset();
    } else if (savedProgress) {
      this.currentStepIndex = savedProgress.currentStepIndex;
      this.stepIdsCompleted = new Set(savedProgress.stepIdsCompleted);
      
      // REC-006: Validate step index is within bounds
      if (this.currentStepIndex >= this.steps.length) {
        if (import.meta.env.DEV) {
          console.log('[onboarding] Saved step index out of bounds, starting from beginning');
        }
        this.currentStepIndex = 0;
      }
      
      // REC-006: Check if all steps are already completed
      if (this.stepIdsCompleted.size >= this.steps.length) {
        if (import.meta.env.DEV) {
          console.log('[onboarding] All steps already completed, marking as done');
        }
        Onboarding.markCompleted();
        this.isActive = false;
        this.cleanup();
        return;
      }
      
      if (import.meta.env.DEV) {
        console.log(`[onboarding] Resuming from step ${this.currentStepIndex + 1}/${this.steps.length}`);
      }
    } else {
      this.currentStepIndex = 0;
      this.stepIdsCompleted = new Set();
    }

    this.injectStyles();
    this.setupEventListeners();
    this.showStep(this.currentStepIndex);
    
    if (import.meta.env.DEV) {
      console.log('[onboarding] Started');
    }
  }

  /**
   * Restart onboarding from the beginning
   */
  restart(): void {
    Onboarding.reset();
    this.currentStepIndex = 0;
    this.stepIdsCompleted = new Set();
    this.isActive = false;
    this.cleanup();
    this.clearProgress();
    this.start();
  }

  /**
   * Skip onboarding - saves progress to resume later
   */
  skip(): void {
    Onboarding.markSkipped();
    Onboarding.saveProgress(this.currentStepIndex, Array.from(this.stepIdsCompleted), this.steps.length);
    this.isActive = false;
    this.cleanup();
    dispatchStateChange({ type: 'skipped' });
    window.dispatchEvent(new CustomEvent('onboarding:skipped'));
  }

  private setupEventListeners(): void {
    // Keyboard navigation
    this.keyboardHandler = (e: KeyboardEvent) => {
      if (!this.isActive) return;
      
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          this.skip();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.nextStep();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          this.prevStep();
          break;
        case 'Tab':
          this.trapFocus(e);
          break;
      }
    };
    document.addEventListener('keydown', this.keyboardHandler);

    // Handle resize
    this.resizeHandler = () => {
      if (this.isActive) {
        this.updateTooltipPosition();
        this.updateHighlightPosition();
      }
    };
    window.addEventListener('resize', this.resizeHandler);
  }

  private showStep(index: number): void {
    if (index < 0 || index >= this.steps.length) {
      this.complete();
      return;
    }

    const step = this.steps[index];
    
    // Idempotency check: skip if this exact step was already completed
    if (this.stepIdsCompleted.has(step.id)) {
      // If we're trying to show a completed step, move to the next uncompleted step
      const nextUncompletedIndex = this.steps.findIndex((s, i) => i > index && !this.stepIdsCompleted.has(s.id));
      if (nextUncompletedIndex !== -1) {
        this.showStep(nextUncompletedIndex);
      } else {
        this.complete();
      }
      return;
    }

    this.currentStepIndex = index;
    this.currentStepMissingTarget = false;

    // Clear previous elements
    this.clearElements();

    // Create backdrop
    this.createBackdrop();

    // Create highlight if target exists
    if (step.targetSelector) {
      this.createHighlight(step);
    }

    // Create tooltip
    this.createTooltip(step);

    // Update position after a brief delay to ensure DOM is ready
    requestAnimationFrame(() => {
      this.updateTooltipPosition();
      this.updateHighlightPosition();
      this.tooltipElement?.querySelector<HTMLElement>('[data-action="next"]')?.focus({ preventScroll: true });
    });

    // Save progress
    Onboarding.saveProgress(this.currentStepIndex, Array.from(this.stepIdsCompleted), this.steps.length);
  }

  private createBackdrop(): void {
    this.backdropElement = document.createElement('div');
    this.backdropElement.className = 'onboarding-backdrop';
    this.backdropElement.addEventListener('click', () => this.nextStep());
    document.body.appendChild(this.backdropElement);
  }

  private createHighlight(step: OnboardingStep): void {
    const target = document.querySelector(step.targetSelector!);
    if (!target) {
      this.currentStepMissingTarget = true;
      console.warn(`[onboarding] Target not found: ${step.targetSelector} — using centered fallback`);
      return;
    }

    this.highlightElement = document.createElement('div');
    this.highlightElement.className = 'onboarding-highlight';
    
    // Prevent clicks on highlight from propagating
    this.highlightElement.addEventListener('click', (e) => {
      e.stopPropagation();
      this.nextStep();
    });
    
    document.body.appendChild(this.highlightElement);
  }

  private createTooltip(step: OnboardingStep): void {
    this.tooltipElement = document.createElement('div');
    this.tooltipElement.className = `onboarding-tooltip onboarding-tooltip--${step.position || 'center'}`;
    this.tooltipElement.setAttribute('role', 'dialog');
    this.tooltipElement.setAttribute('aria-modal', 'true');
    this.tooltipElement.setAttribute('aria-labelledby', 'onboarding-step-title');
    this.tooltipElement.setAttribute('aria-describedby', 'onboarding-step-copy');

    const progressDots = this.steps.map((_, i) => 
      `<span class="onboarding-dot ${i === this.currentStepIndex ? 'active' : ''} ${i < this.currentStepIndex ? 'completed' : ''}"></span>`
    ).join('');

    const actionText = step.action || (step.position === 'center' ? 'Click anywhere to continue' : 'Click to continue');

    this.tooltipElement.innerHTML = `
      <div class="onboarding-tooltip__header">
        <h3 class="onboarding-tooltip__title" id="onboarding-step-title">${step.title}</h3>
        <div class="onboarding-tooltip__actions">
          <button class="onboarding-tooltip__restart" data-action="restart" aria-label="Restart onboarding">Restart</button>
          <button class="onboarding-tooltip__skip" data-action="skip" aria-label="Skip onboarding">Skip</button>
        </div>
      </div>
      <div class="onboarding-tooltip__content">
        <p id="onboarding-step-copy">${step.content}</p>
      </div>
      <div class="onboarding-tooltip__footer">
        <div class="onboarding-progress">${progressDots}</div>
        <div class="onboarding-nav">
          ${this.currentStepIndex > 0 ? 
            `<button class="onboarding-btn onboarding-btn--secondary" data-action="prev">← Back</button>` : 
            ''}
          <button class="onboarding-btn onboarding-btn--primary" data-action="next">
            ${this.currentStepIndex === this.steps.length - 1 ? 'Enter the garden' : 'Next →'}
          </button>
        </div>
      </div>
      ${this.currentStepMissingTarget ? '<div class="onboarding-fallback-note">This part of the interface is unavailable right now. Continuing with a centered guide.</div>' : ''}
      <div class="onboarding-action-hint">${actionText}</div>
    `;

    // Add event listeners
    const restartBtn = this.tooltipElement.querySelector('[data-action="restart"], .onboarding-tooltip__restart');
    restartBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.restart();
    });

    const skipBtn = this.tooltipElement.querySelector('[data-action="skip"], .onboarding-tooltip__skip');
    skipBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.skip();
    });

    const prevBtn = this.tooltipElement.querySelector('[data-action="prev"]');
    prevBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.prevStep();
    });

    const nextBtn = this.tooltipElement.querySelector('[data-action="next"]');
    nextBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.nextStep();
    });

    // Click on tooltip content shouldn't advance
    this.tooltipElement.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    document.body.appendChild(this.tooltipElement);
  }

  private updateTooltipPosition(): void {
    if (!this.tooltipElement) return;

    const step = this.steps[this.currentStepIndex];
    const tooltip = this.tooltipElement;
    const isMobile = window.innerWidth <= 480;

    // On mobile, always center the tooltip
    if (isMobile || step.position === 'center' || !step.targetSelector) {
      tooltip.style.position = 'fixed';
      tooltip.style.top = '50%';
      tooltip.style.left = '50%';
      tooltip.style.right = 'auto';
      tooltip.style.transform = 'translate(-50%, -50%)';
      tooltip.style.width = isMobile ? 'calc(100vw - 32px)' : '';
      tooltip.style.maxWidth = isMobile ? 'none' : '380px';
      return;
    }

    const target = document.querySelector(step.targetSelector);
    if (!target) {
      this.currentStepMissingTarget = true;
      // Fallback to center if target not found
      tooltip.style.position = 'fixed';
      tooltip.style.top = '50%';
      tooltip.style.left = '50%';
      tooltip.style.right = 'auto';
      tooltip.style.transform = 'translate(-50%, -50%)';
      tooltip.style.width = isMobile ? 'calc(100vw - 32px)' : '';
      tooltip.style.maxWidth = isMobile ? 'none' : '380px';
      return;
    }

    const rect = target.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const padding = step.highlightPadding || 10;
    const gap = 16;

    let top = 0;
    let left = 0;

    switch (step.position) {
      case 'top':
        top = rect.top - tooltipRect.height - gap;
        left = rect.left + (rect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = rect.bottom + gap;
        left = rect.left + (rect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        top = rect.top + (rect.height - tooltipRect.height) / 2;
        left = rect.left - tooltipRect.width - gap;
        break;
      case 'right':
        top = rect.top + (rect.height - tooltipRect.height) / 2;
        left = rect.right + gap;
        break;
      default:
        top = rect.bottom + gap;
        left = rect.left + (rect.width - tooltipRect.width) / 2;
    }

    // Keep in viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 16;

    left = Math.max(margin, Math.min(left, viewportWidth - tooltipRect.width - margin));
    top = Math.max(margin, Math.min(top, viewportHeight - tooltipRect.height - margin));

    tooltip.style.position = 'fixed';
    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;
    tooltip.style.right = 'auto';
    tooltip.style.transform = 'none';
    tooltip.style.width = '';
  }

  private updateHighlightPosition(): void {
    if (!this.highlightElement) return;

    const step = this.steps[this.currentStepIndex];
    if (!step.targetSelector) return;

    const target = document.querySelector(step.targetSelector);
    if (!target) return;

    const rect = target.getBoundingClientRect();
    const padding = step.highlightPadding || 10;

    this.highlightElement.style.position = 'fixed';
    this.highlightElement.style.top = `${rect.top - padding}px`;
    this.highlightElement.style.left = `${rect.left - padding}px`;
    this.highlightElement.style.width = `${rect.width + padding * 2}px`;
    this.highlightElement.style.height = `${rect.height + padding * 2}px`;
  }

  private nextStep(): void {
    // Mark current step as completed before moving
    const currentStep = this.steps[this.currentStepIndex];
    if (currentStep) {
      this.stepIdsCompleted.add(currentStep.id);
    }

    if (this.currentStepIndex < this.steps.length - 1) {
      this.showStep(this.currentStepIndex + 1);
      dispatchStateChange({ 
        type: 'progress', 
        stepIndex: this.currentStepIndex,
        stepId: currentStep?.id
      });
    } else {
      this.complete();
    }
  }

  private prevStep(): void {
    if (this.currentStepIndex > 0) {
      // Remove current step from completed when going back
      const currentStep = this.steps[this.currentStepIndex];
      if (currentStep) {
        this.stepIdsCompleted.delete(currentStep.id);
      }
      this.showStep(this.currentStepIndex - 1);
    }
  }

  private complete(): void {
    // Mark all steps as completed
    this.steps.forEach(step => this.stepIdsCompleted.add(step.id));
    Onboarding.saveProgress(this.currentStepIndex, Array.from(this.stepIdsCompleted), this.steps.length);

    Onboarding.markCompleted();
    this.clearProgress();
    this.isActive = false;
    this.cleanup();
    if (import.meta.env.DEV) {
      console.log('[onboarding] Completed');
    }
    
    // GARDEN-137: Track onboarding completion
    analytics.trackFeatureUsage('onboarding_complete');
    
    // Dispatch events for other components
    dispatchStateChange({ type: 'completed' });
    window.dispatchEvent(new CustomEvent('onboarding:completed'));
  }

  /**
   * Clear saved progress from localStorage
   */
  private clearProgress(): void {
    stateClearProgress();
  }

  private clearElements(): void {
    if (this.tooltipElement) {
      this.tooltipElement.remove();
      this.tooltipElement = null;
    }
    if (this.highlightElement) {
      this.highlightElement.remove();
      this.highlightElement = null;
    }
    if (this.backdropElement) {
      this.backdropElement.remove();
      this.backdropElement = null;
    }
  }

  private trapFocus(event: KeyboardEvent): void {
    if (!this.tooltipElement) return;
    const focusable = Array.from(
      this.tooltipElement.querySelectorAll<HTMLElement>('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'),
    ).filter((element) => element.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private lockBackground(): void {
    this.backgroundInertStates.clear();
    ['#app', '#garden-shell', '#social-ui-container', '.garden-controls-menu'].forEach((selector) => {
      document.querySelectorAll<HTMLElement>(selector).forEach((element) => {
        if (this.backgroundInertStates.has(element)) return;
        this.backgroundInertStates.set(element, element.inert);
        element.inert = true;
      });
    });
  }

  private unlockBackground(): void {
    this.backgroundInertStates.forEach((wasInert, element) => {
      if (element.isConnected) element.inert = wasInert;
    });
    this.backgroundInertStates.clear();
  }

  private cleanup(): void {
    this.clearElements();
    this.unlockBackground();

    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler);
      this.keyboardHandler = null;
    }

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }

    if (this.previousFocus?.isConnected) {
      this.previousFocus.focus({ preventScroll: true });
    }
    this.previousFocus = null;
  }

  private injectStyles(): void {
    const styleId = 'onboarding-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .onboarding-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(13, 17, 23, 0.85);
        backdrop-filter: blur(3px);
        z-index: var(--z-onboarding);
        animation: onboarding-fade-in 180ms ease-out;
        cursor: pointer;
      }

      .onboarding-highlight {
        position: fixed;
        border-radius: 6px;
        box-shadow: 0 0 0 4px #238636, 0 0 0 9999px rgba(13, 17, 23, 0.85);
        z-index: calc(var(--z-onboarding) + 1);
        cursor: pointer;
        pointer-events: auto;
      }

      .onboarding-tooltip {
        position: fixed;
        background: rgba(22, 27, 34, 0.98);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        padding: 24px;
        max-width: 380px;
        min-width: 300px;
        z-index: calc(var(--z-onboarding) + 2);
        box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05);
        animation: onboarding-tooltip-in 200ms cubic-bezier(0.2, 0.8, 0.2, 1);
        font-family: var(--font-body);
        color: #e6edf3;
      }

      @keyframes onboarding-fade-in { from { opacity: 0; } to { opacity: 1; } }
      @keyframes onboarding-tooltip-in {
        from { opacity: 0; transform: translateY(10px) scale(0.95); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }

      .onboarding-tooltip__header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 12px; }
      .onboarding-tooltip__title { margin: 0; font-size: 18px; font-weight: 700; color: #fff; line-height: 1.3; }
      .onboarding-tooltip__actions { display: flex; gap: 6px; align-items: center; }
      .onboarding-tooltip__restart,
      .onboarding-tooltip__skip {
        background: transparent;
        border: none;
        color: #8b949e;
        font-size: 13px;
        cursor: pointer;
        min-height: 44px;
        padding: 8px 10px;
        border-radius: 5px;
        transition: transform var(--duration-fast) var(--ease-out), background-color var(--duration-fast) ease, color var(--duration-fast) ease;
      }

      .onboarding-tooltip__content { margin-bottom: 20px; }
      .onboarding-tooltip__content p { margin: 0; font-size: 15px; line-height: 1.6; color: #c9d1d9; }

      .onboarding-tooltip__footer { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding-top: 16px; border-top: 1px solid rgba(255, 255, 255, 0.06); }
      .onboarding-progress { display: flex; gap: 6px; }
      .onboarding-dot { width: 8px; height: 8px; border-radius: 50%; background: rgba(255, 255, 255, 0.15); transition: transform var(--duration-fast) var(--ease-out), background-color var(--duration-fast) ease; }
      .onboarding-dot.active { background: #238636; transform: scale(1.2); }
      .onboarding-dot.completed { background: #3fb950; }
      .onboarding-nav { display: flex; gap: 8px; }

      .onboarding-btn { padding: 8px 16px; border-radius: 6px; font-size: 14px; font-weight: 700; cursor: pointer; transition: transform var(--duration-fast) var(--ease-out), background-color var(--duration-fast) ease, color var(--duration-fast) ease; border: none; min-height: 44px; }
      .onboarding-btn:active,
      .onboarding-tooltip__restart:active,
      .onboarding-tooltip__skip:active { transform: scale(0.97); }
      .onboarding-btn--primary { background: #238636; color: white; }
      .onboarding-btn--secondary { background: rgba(255, 255, 255, 0.08); color: #c9d1d9; }

      @media (hover: hover) and (pointer: fine) {
        .onboarding-tooltip__restart:hover { color: #58a6ff; background: rgba(88, 166, 255, 0.12); }
        .onboarding-tooltip__skip:hover { color: #f85149; background: rgba(248, 81, 73, 0.1); }
        .onboarding-btn--primary:hover { background: #2ea043; transform: translateY(-1px); }
        .onboarding-btn--secondary:hover { background: rgba(255, 255, 255, 0.12); }
      }

      .onboarding-fallback-note {
        margin-top: 8px;
        padding: 8px 10px;
        border-radius: 8px;
        font-size: 12px;
        color: #ffd866;
        background: rgba(255, 216, 102, 0.1);
        border: 1px solid rgba(255, 216, 102, 0.35);
      }
      .onboarding-action-hint { margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(255, 255, 255, 0.06); font-size: 12px; color: #8b949e; text-align: center; }

      @media (max-width: 768px) {
        .onboarding-tooltip {
          top: 50% !important;
          left: 50% !important;
          right: auto !important;
          transform: translate(-50%, -50%) !important;
          width: calc(100vw - 24px) !important;
          max-width: none !important;
          min-width: 0;
          padding: 18px;
          box-sizing: border-box;
        }
        .onboarding-tooltip__title { font-size: 15px; }
        .onboarding-tooltip__actions { gap: 2px; }
        .onboarding-tooltip__content p { font-size: 12px; }
        .onboarding-tooltip__footer { flex-direction: column; gap: 12px; align-items: stretch; }
        .onboarding-progress { justify-content: center; order: -1; }
        .onboarding-nav { justify-content: space-between; }
        .onboarding-btn { flex: 1; min-height: 44px; font-size: 13px; }
        .onboarding-action-hint { font-size: 9px; }
        .onboarding-highlight { display: none; }
      }

      @media (prefers-contrast: high) {
        .onboarding-highlight { box-shadow: 0 0 0 4px #fff, 0 0 0 9999px rgba(0, 0, 0, 0.9); }
        .onboarding-tooltip { border-width: 2px; border-color: #fff; }
      }

      @media (prefers-reduced-motion: reduce) {
        .onboarding-backdrop,
        .onboarding-tooltip,
        .onboarding-highlight { animation: none; }
      }
    `;
    document.head.appendChild(style);
  }

  onResize(): void {
    if (this.isActive) {
      this.updateTooltipPosition();
      this.updateHighlightPosition();
    }
  }

  destroy(): void {
    this.isActive = false;
    this.cleanup();
  }
}

// Lazy singleton instance for convenience
let _onboardingInstance: Onboarding | null = null;
export function getOnboarding(): Onboarding {
  if (!_onboardingInstance) {
    _onboardingInstance = new Onboarding();
  }
  return _onboardingInstance;
}

// Singleton export with lazy initialization
// Uses prototype chaining to pass instanceof checks
function createOnboardingProxy(): Onboarding {
  const handler = {
    get(target: any, prop: string | symbol) {
      const instance = getOnboarding();
      const value = (instance as any)[prop];
      return typeof value === 'function' ? value.bind(instance) : value;
    }
  };
  
  const proxy = new Proxy(function() {}, handler);
  Object.setPrototypeOf(proxy, Onboarding.prototype);
  return proxy as unknown as Onboarding;
}

export const onboarding: Onboarding = createOnboardingProxy();

// Auto-start helper - call this when garden is ready
export function initOnboarding(): void {
  // Only start if not completed and user is authenticated
  if (!Onboarding.hasCompleted() && document.querySelector('#garden-canvas, canvas')) {
    // Small delay to ensure UI is fully rendered
    setTimeout(() => {
      getOnboarding().start();
    }, 1000);
  }
}
